import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo } from 'react';
import { formatDate } from 'src/utils';
import { useNavigate } from 'react-router-dom';
import { WorkTicketDto } from 'src/api/FiretrolApiClient';
import { useJobs } from 'src/hooks/useJobs';
import { ViewActionIcon } from 'src/design/atoms/ViewActionIcon';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';

interface ITicketsTableProps {
  tickets: WorkTicketDto[];
  loading?: boolean;
}

export const TicketsTable: React.FC<ITicketsTableProps> = ({
  tickets,
  loading = true,
}) => {
  const navigate = useNavigate();
  const { selectedJob } = useJobs();

  const getColumns = React.useCallback((): MRT_ColumnDef<WorkTicketDto>[] => {
    return [
      {
        accessorKey: 'ticketNumber',
        header: 'Ticket Number',
        size: 150,
      },
      {
        accessorKey: 'summary',
        header: 'Summary',
        size: 300,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 120,
      },
      {
        accessorKey: 'requestedBy.fullName',
        header: 'Requested By',
        size: 150,
      },
      {
        accessorKey: 'date',
        header: 'Date Requested',
        size: 120,
        Cell: ({ cell }) => {
          const date = cell.getValue<Date>();
          return date ? formatDate(date) : '';
        },
      },
      {
        accessorKey: 'createdBy.fullName',
        header: 'Created By',
        accessorFn: (row) => row.createdBy?.fullName || '',
        size: 150,
      },
    ];
  }, []);

  const columns = useMemo(() => getColumns(), [getColumns]);

  const tableState = useTableState({
    identifier: 'tickets',
    columns: columns,
    defaultSorting: [{ id: 'date', desc: true }],
  });

  const tableExports = useTableExports<WorkTicketDto>(
    'work-tickets',
    (data) => {
      return data.map((item) => ({
        ['Job Name']: selectedJob?.name,
        ['Job Number']: selectedJob?.jobNumber,
        ['Ticket Number']: item.ticketNumber,
        ['Summary']: item.summary || '',
        ['Status']: item.status || '',
        ['Requested By']: item.requestedBy?.fullName || '',
        ['Date Requested']: item.date ? formatDate(item.date) : '',
        ['Created By']: item.createdBy?.fullName || '',
      }));
    },
  );

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: true,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 100 } },
      renderRowActions: ({ row }) => {
        const ticket = row.original;
        return (
          <ViewActionIcon
            onClick={() =>
              navigate(`/jobs/${ticket.jobId}/tickets/view/${ticket.id}`)
            }
          />
        );
      },
      enableClickToCopy: false,
      columns: columns,
      data: tickets,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
    },
    getRowId: (row) => String(row.id),
    totalCount: tickets.length,
    isLoading: loading,
    isFetching: false,
    isError: false,
    ...tableExports,
  });

  return (
    <div style={{ height: 600, width: '100%', backgroundColor: '#fff' }}>
      {table}
    </div>
  );
};
